import { CompositeNavigationProp, CompositeScreenProps, NavigationProp } from '@react-navigation/native'
import { createStackNavigator, StackNavigationProp, StackScreenProps } from '@react-navigation/stack'
import { CountryCode } from 'libphonenumber-js'
import React, { lazy } from 'react'
import { useTranslation } from 'react-i18next'

import { SocialData } from '../@types'
import CustomHeaderBackButton from '../components/CustomHeaderBackButton/CustomHeaderBackButton'
import HeaderLogo from '../components/HeaderLogo/HeaderLogo'
import { useAuth } from '../context/auth'
import { AccountType } from '../hooks/use-create-standard-profile'
import { useWhiteScreenOptions } from '../hooks/use-screen-options'
import { StandardAccountFormValues } from '../screens/CreateAccountProfile/CreateAccountProfile'
import { isUserAuthenticated } from '../util/auth'

import { hideTokenInPath } from './helpers'

import { RootStackNavigationProp, RootStackParamList, RootStackScreenProps } from '.'

const AuthRedirect = lazy(() => import('../screens/AuthRedirect'))
const CreateAccountProfile = lazy(() => import('../screens/CreateAccountProfile'))
const CreateAccountVerify = lazy(() => import('../screens/CreateAccountVerify'))
const ForgotPasswordNewPassword = lazy(() => import('../screens/ForgotPasswordNewPassword'))
const ForgotPasswordPhone = lazy(() => import('../screens/ForgotPasswordPhone'))
const ForgotPasswordVerify = lazy(() => import('../screens/ForgotPasswordVerify'))
const SignInMFA = lazy(() => import('../screens/SignInMFA'))

export type AuthStackParamList = {
  CreateAccountVerify: {
    accountType: AccountType
    token: string
    phone: string
    countryCode: CountryCode
    user?: StandardAccountFormValues
    userId?: number
  }
  CreateAccountProfile:
    | {
        isPhoneVerified: boolean
        token?: string
        phone?: string
        socialAccountData?: SocialData
        countryCode?: CountryCode
      }
    | undefined
  SignInMFA: { token: string; phone?: string; countryCode?: CountryCode; maskedPhone?: string }
  ForgotPasswordPhone: undefined
  ForgotPasswordVerify: { token: string }
  ForgotPasswordNewPassword: { token: string }
  AuthRedirect: { reason?: string } | undefined
}

export type AuthStackScreenProps<T extends keyof AuthStackParamList> = CompositeScreenProps<
  StackScreenProps<AuthStackParamList, T>,
  RootStackScreenProps<keyof RootStackParamList>
>

export type AuthStackNavigationProp<T extends keyof AuthStackParamList> = CompositeNavigationProp<
  StackNavigationProp<AuthStackParamList, T>,
  RootStackNavigationProp<keyof RootStackParamList>
>

const AuthStack = createStackNavigator<AuthStackParamList>()

export const authStackLinking = {
  screens: {
    CreateAccountVerify: {
      path: '/account/create/verify/:token',
      ...hideTokenInPath,
    },
    CreateAccountProfile: {
      path: '/account/create/profile',
      ...hideTokenInPath,
    },
    SignInMFA: {
      path: '/account/signin/mfa',
      ...hideTokenInPath,
    },
    ForgotPasswordPhone: '/account/forgot',
    ForgotPasswordVerify: {
      path: '/account/forgot/verify',
      ...hideTokenInPath,
    },
    ForgotPasswordNewPassword: {
      path: '/account/forgot/new-password',
      ...hideTokenInPath,
    },
  },
}

export function Auth() {
  const { t } = useTranslation()
  const whiteScreenOptions = useWhiteScreenOptions()
  const { profile } = useAuth()

  const getHeaderBackButton = (navigation: NavigationProp<AuthStackParamList>) => (
    <CustomHeaderBackButton callRedirect={() => navigation.navigate('AuthRedirect')} navigation={navigation} />
  )

  return (
    <AuthStack.Navigator screenOptions={whiteScreenOptions}>
      {isUserAuthenticated({ profile }) ? (
        <AuthStack.Screen name="AuthRedirect" component={AuthRedirect} />
      ) : (
        <>
          <AuthStack.Screen
            name="SignInMFA"
            component={SignInMFA}
            options={({ navigation }) => ({
              headerTitle: () => <HeaderLogo />,
              title: t('mfaCodeCheck.title'),
              headerLeft: () => getHeaderBackButton(navigation),
            })}
          />
          <AuthStack.Screen
            name="CreateAccountVerify"
            component={CreateAccountVerify}
            options={({ navigation }) => ({
              headerTitle: () => <HeaderLogo />,
              title: t('mfaCodeCheck.title'),
              headerLeft: () => getHeaderBackButton(navigation),
            })}
          />
          <AuthStack.Screen
            name="ForgotPasswordPhone"
            component={ForgotPasswordPhone}
            options={({ navigation }) => ({
              headerTitle: () => <HeaderLogo />,
              title: t('screens.forgotPasswordPhone.title'),
              headerLeft: () => getHeaderBackButton(navigation),
            })}
          />
          <AuthStack.Screen
            name="ForgotPasswordVerify"
            component={ForgotPasswordVerify}
            options={({ navigation }) => ({
              headerTitle: () => <HeaderLogo />,
              title: t('mfaCodeCheck.title'),
              headerLeft: () => getHeaderBackButton(navigation),
            })}
          />
          <AuthStack.Screen
            name="ForgotPasswordNewPassword"
            component={ForgotPasswordNewPassword}
            options={({ navigation }) => ({
              headerTitle: () => <HeaderLogo />,
              title: t('screens.forgotPasswordNewPassword.title'),
              headerLeft: () => getHeaderBackButton(navigation),
            })}
          />
          <AuthStack.Screen
            name="CreateAccountProfile"
            component={CreateAccountProfile}
            options={({ navigation }) => ({
              headerTitle: () => <HeaderLogo />,
              title: t('screens.createAccountProfile.title'),
              headerLeft: () => getHeaderBackButton(navigation),
            })}
          />
        </>
      )}
    </AuthStack.Navigator>
  )
}

export default Auth
